import { useEffect, useState, useRef } from 'react';
import confetti from 'canvas-confetti';

/**
 * PokemonCaptureAnimation
 *
 * Renders the classic Pokémon capture sequence as a fullscreen overlay:
 *  1. Pokémon sprite enters and idle-bounces
 *  2. A pokéball arcs in from the right, spinning
 *  3. The pokéball "hits" the sprite, sprite flashes white and shrinks into the ball
 *  4. The ball drops to ground level
 *  5. The ball shakes 3 times (with subtle "click" pulses)
 *  6. A burst of stars + confetti + "POKÉMON CAPTURADO!" overlay
 *  7. onDone fires, parent shows the reward card
 *
 * Props:
 *  - spriteUrl: image of the Pokémon being captured
 *  - name: Pokémon name (displayed at the end)
 *  - firstCapture: whether this is a brand-new dex entry
 *  - onDone: callback when the sequence ends
 */
const POKEBALL_SVG = (
  // Classic red/white pokéball, drawn as SVG so it's crisp at any size
  <svg viewBox="0 0 100 100" className="w-full h-full">
    <defs>
      <radialGradient id="pkb_top" cx="50%" cy="40%" r="60%">
        <stop offset="0%" stopColor="#ff6b6b"/>
        <stop offset="100%" stopColor="#c0392b"/>
      </radialGradient>
      <radialGradient id="pkb_bot" cx="50%" cy="60%" r="60%">
        <stop offset="0%" stopColor="#ffffff"/>
        <stop offset="100%" stopColor="#cbd5e1"/>
      </radialGradient>
    </defs>
    {/* top red half */}
    <path d="M 5,50 A 45,45 0 0 1 95,50 Z" fill="url(#pkb_top)" stroke="#0f172a" strokeWidth="4"/>
    {/* bottom white half */}
    <path d="M 95,50 A 45,45 0 0 1 5,50 Z" fill="url(#pkb_bot)" stroke="#0f172a" strokeWidth="4"/>
    {/* horizontal divider */}
    <line x1="5" y1="50" x2="95" y2="50" stroke="#0f172a" strokeWidth="4"/>
    {/* center circle (button) */}
    <circle cx="50" cy="50" r="12" fill="#f8fafc" stroke="#0f172a" strokeWidth="4"/>
    <circle cx="50" cy="50" r="5" fill="#0f172a"/>
  </svg>
);

export default function PokemonCaptureAnimation({ spriteUrl, name, firstCapture, onDone }) {
  // Phases:
  // 0: pokemon idle
  // 1: ball flying in
  // 2: hit (flash + shrink)
  // 3: ball drops to floor
  // 4: shake 1
  // 5: shake 2
  // 6: shake 3
  // 7: captured! reveal
  const [phase, setPhase] = useState(0);
  const fired = useRef(false);

  useEffect(() => {
    const timeouts = [];
    const schedule = (ms, fn) => timeouts.push(setTimeout(fn, ms));

    schedule(450,  () => setPhase(1));   // ball flies in
    schedule(1100, () => setPhase(2));   // hit + flash
    schedule(1500, () => setPhase(3));   // ball drops
    schedule(1900, () => setPhase(4));   // shake 1
    schedule(2350, () => setPhase(5));   // shake 2
    schedule(2800, () => setPhase(6));   // shake 3
    schedule(3300, () => {               // captured!
      setPhase(7);
      if (!fired.current) {
        fired.current = true;
        // Confetti burst
        confetti({
          particleCount: 130,
          spread: 90,
          startVelocity: 45,
          origin: { y: 0.55 },
          colors: ['#fbbf24', '#ef4444', '#10b981', '#3b82f6', '#a855f7'],
        });
        confetti({
          particleCount: 80,
          angle: 60,
          spread: 60,
          origin: { x: 0, y: 0.7 },
          colors: ['#fbbf24', '#ef4444', '#fef9c3'],
        });
        confetti({
          particleCount: 80,
          angle: 120,
          spread: 60,
          origin: { x: 1, y: 0.7 },
          colors: ['#fbbf24', '#ef4444', '#fef9c3'],
        });
      }
    });
    schedule(5300, () => onDone && onDone());  // close

    return () => timeouts.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-slate-900/90 via-slate-900/85 to-amber-950/80 backdrop-blur-sm"
      data-testid="pokemon-capture-animation"
      style={{ animation: 'pkfade-in 0.25s ease-out' }}
    >
      <style>{`
        @keyframes pkfade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pk-idle-bounce {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes pk-throw {
          0%   { right: -180px; top: 8%;   transform: rotate(-720deg) scale(0.6); }
          70%  { right: 50%;    top: 35%;  transform: translate(50%,0) rotate(0deg) scale(1.1); }
          100% { right: 50%;    top: 42%;  transform: translate(50%,0) rotate(20deg) scale(1); }
        }
        @keyframes pk-flash {
          0% { opacity: 1; transform: scale(1); filter: brightness(1); }
          40% { opacity: 1; transform: scale(1.15); filter: brightness(8); }
          70% { opacity: 0.4; transform: scale(0.4); filter: brightness(20); }
          100% { opacity: 0; transform: scale(0.05); filter: brightness(40); }
        }
        @keyframes pk-drop {
          0%   { top: 42%; }
          80%  { top: 70%; }
          90%  { top: 67%; }
          100% { top: 70%; }
        }
        @keyframes pk-shake {
          0%, 100% { transform: translate(-50%, 0) rotate(0deg); }
          25% { transform: translate(-50%, 0) rotate(-20deg); }
          50% { transform: translate(-50%, 0) rotate(0deg); }
          75% { transform: translate(-50%, 0) rotate(18deg); }
        }
        @keyframes pk-burst {
          0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          50% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes pk-ring {
          0% { transform: translate(-50%,-50%) scale(0.2); opacity: 0.9; }
          100% { transform: translate(-50%,-50%) scale(3); opacity: 0; }
        }
      `}</style>

      {/* Pokémon sprite */}
      {phase < 2 && (
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            top: '32%',
            animation: 'pk-idle-bounce 1s ease-in-out infinite',
          }}
        >
          <img
            src={spriteUrl}
            alt={name}
            className="w-44 h-44 sm:w-56 sm:h-56"
            style={{ imageRendering: 'pixelated', filter: 'drop-shadow(0 8px 8px rgba(0,0,0,0.4))' }}
          />
        </div>
      )}

      {/* Pokémon white-out flash (phase 2) */}
      {phase === 2 && (
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            top: '32%',
            animation: 'pk-flash 0.4s ease-in forwards',
          }}
        >
          <img
            src={spriteUrl}
            alt={name}
            className="w-44 h-44 sm:w-56 sm:h-56"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>
      )}

      {/* Pokéball thrown */}
      {phase >= 1 && phase < 7 && (
        <div
          className="absolute w-20 h-20 sm:w-24 sm:h-24"
          style={
            phase === 1
              ? { right: '-180px', top: '8%', animation: 'pk-throw 0.55s cubic-bezier(.4,.2,.3,1) forwards' }
              : phase === 2
              ? { left: '50%', top: '42%', transform: 'translate(-50%, 0) rotate(20deg)' }
              : phase === 3
              ? { left: '50%', top: '70%', transform: 'translate(-50%, 0)', animation: 'pk-drop 0.4s ease-in forwards' }
              : { // phases 4–6: shake at floor
                  left: '50%',
                  top: '70%',
                  animation: 'pk-shake 0.45s ease-in-out',
                }
          }
          key={`ball-${phase}`}
        >
          {POKEBALL_SVG}
        </div>
      )}

      {/* Captured burst */}
      {phase === 7 && (
        <>
          {/* glowing ring expanding */}
          <div
            className="absolute left-1/2 top-1/2 w-40 h-40 rounded-full border-4 border-amber-300"
            style={{ animation: 'pk-ring 0.7s ease-out forwards' }}
          />
          <div
            className="absolute left-1/2 top-1/2 w-40 h-40 rounded-full border-4 border-rose-400"
            style={{ animation: 'pk-ring 0.9s ease-out 0.15s forwards' }}
          />
          {/* card */}
          <div
            className="absolute left-1/2 top-1/2 max-w-xs w-[88%] sm:w-auto px-6 py-5 rounded-3xl border-[4px] border-slate-900 shadow-[6px_6px_0_rgba(15,23,42,0.95)] bg-gradient-to-br from-amber-50 via-white to-rose-50 text-center"
            style={{ animation: 'pk-burst 0.6s cubic-bezier(.34,1.56,.64,1) forwards' }}
          >
            <p className="text-[11px] font-outfit font-black uppercase tracking-[0.25em] text-amber-700">
              {firstCapture ? '★ Primeira captura ★' : 'Pokémon adicionado'}
            </p>
            <img
              src={spriteUrl}
              alt={name}
              className="w-32 h-32 sm:w-40 sm:h-40 mx-auto my-2"
              style={{ imageRendering: 'pixelated' }}
            />
            <p className="font-outfit font-black text-2xl sm:text-3xl text-slate-900">{name}</p>
            <p className="text-xs text-slate-600 italic mt-1">foi capturado!</p>
          </div>
        </>
      )}
    </div>
  );
}
